import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUrlState } from '@/lib/useUrlState';
import { aktePfad } from '@/lib/contact-path';
import type { Audience, Tag } from '@epasu/core';
import { ListPlus, Search, Tags as TagsIcon, UserCheck } from 'lucide-react';
import { crm, type ContactRow, type LeadListRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/PageHeader';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useDebouncedSearch } from '@/lib/useDebouncedSearch';
import { useQuerySeededState } from '@/lib/useQuerySeededState';
import { SaveAsListDialog } from './SaveAsListDialog';
import { TagSelectionDialog } from './TagSelectionDialog';
import { LeadRow } from './LeadRow';
import { useLeadZusatz } from './useLeadZusatz';
import { ZUSTAND_LABEL, ZUSTAND_REIHENFOLGE, type Zustand } from './lead-zustand';

/**
 * Wie viele Leads auf einmal geladen werden — großzügig genug für den hier
 * zu erwartenden Bestand von einigen hundert kalten Adressen.
 */
const LIMIT = 500;

/**
 * Die Leads-Seite: kalt angeschriebene Adressen, getrennt von den
 * qualifizierten Kontakten, die in der Akte ihrer Firma stehen. Import, Taggen, Listen bilden
 * und Übernehmen in einer Tabelle.
 */
export function LeadsPage() {
  const navigate = useNavigate();

  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [tagsAlle, setTagsAlle] = useState<Tag[]>([]);
  const [listenAlle, setListenAlle] = useState<LeadListRow[]>([]);

  // In der URL statt im Zustand: sonst setzte jeder Ausflug in eine Lead-Akte
  // die Filter zurueck, und „Zurueck“ fuehrte auf die ungefilterte Liste.
  const [audienceId, setAudienceId] = useUrlState<string>('zielgruppe', '');
  const [tagId, setTagId] = useUrlState<string>('tag', '');
  const [zustandFilter, setZustandFilter] = useUrlState<Zustand | ''>('zustand', '', [
    '',
    ...ZUSTAND_REIHENFOLGE,
  ]);
  const [suche, setSuche] = useQuerySeededState('q');

  // Der Listenfilter wird von der URL gesteuert, nicht nur aus ihr gesetzt:
  // `useQuerySeededState` synchronisiert absichtlich nur in eine Richtung
  // (siehe sein Doc-Kommentar) — richtig für die Textsuche, die von der
  // globalen Suche vorbelegt wird und danach in Ruhe weitergetippt werden
  // soll, aber falsch für einen Filter mit eigenem Sprungziel: verschwindet
  // `?liste=` aus der URL (Klick auf „Leads“ in der Seitenleiste), bliebe der
  // Filter sonst hängen, weil React Router bei einer reinen Query-Änderung
  // nicht neu einhängt und es damit auch keinen frischen Anfangswert gibt.
  // `useSearchParams` direkt als einzige Wahrheit vermeidet das strukturell,
  // in beide Richtungen.
  const [searchParams, setSearchParams] = useSearchParams();
  const listId = searchParams.get('liste') ?? '';
  const setListId = useCallback(
    (id: string) => {
      setSearchParams(
        (bisher) => {
          const naechste = new URLSearchParams(bisher);
          if (id) naechste.set('liste', id);
          else naechste.delete('liste');
          return naechste;
        },
        // Sonst sammelt jede Änderung im Auswahlfeld einen Verlaufseintrag,
        // und der Zurück-Knopf klappert durch alte Filterzustände.
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [refreshKey, setRefreshKey] = useState(0);

  const [speichernAlsListe, setSpeichernAlsListe] = useState(false);
  const [taggen, setTaggen] = useState(false);
  const [uebernehmen, setUebernehmen] = useState(false);

  useEffect(() => {
    void crm.audiences.list().then(setAudiences);
  }, []);

  const ladeStammdaten = useCallback(() => {
    void crm.tags.list().then(setTagsAlle);
    // Auch archivierte: eine Liste kann archiviert sein und trotzdem noch
    // Mitglieder haben — sonst zeigte die Listen-Spalte für deren Leads eine
    // leere Zeile statt des Namens.
    void crm.leadLists.list({ includeArchived: true }).then(setListenAlle);
  }, []);
  useEffect(ladeStammdaten, [ladeStammdaten, refreshKey]);

  const {
    data: rowsData,
    loading,
    error: ladeFehler,
  } = useDebouncedSearch<ContactRow[]>(
    () =>
      crm.contacts.list({
        lifecycle: 'lead',
        limit: LIMIT,
        ...(audienceId ? { audienceId } : {}),
        ...(suche.trim() ? { search: suche.trim() } : {}),
      }),
    [audienceId, suche, refreshKey],
  );
  const rows = useMemo(() => rowsData ?? [], [rowsData]);
  const ladeLeads = !ladeFehler && (loading || rowsData === undefined);

  const rowIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const zusatz = useLeadZusatz(rowIds, refreshKey);

  const audienceName = useMemo(
    () => new Map(audiences.map((a) => [a.id, a.name])),
    [audiences],
  );
  const listName = useMemo(
    () => new Map(listenAlle.map((l) => [l.id, l.name])),
    [listenAlle],
  );
  const listenAktiv = useMemo(() => listenAlle.filter((l) => !l.archived), [listenAlle]);

  // Tag- und Listenfilter, sowie der abgeleitete Zustand, greifen erst nach
  // dem Laden — dafür gibt es weder in `contacts.list` noch anderswo einen
  // serverseitigen Filter, und bei einigen hundert Leads ist das clientseitig
  // unproblematisch.
  const gefiltert = useMemo(() => {
    return rows.filter((r) => {
      const info = zusatz.get(r.id);
      if (tagId && !(info?.tags.some((t) => t.id === tagId) ?? false)) return false;
      if (listId && !(info?.listIds.includes(listId) ?? false)) return false;
      if (zustandFilter && info?.zustand !== zustandFilter) return false;
      return true;
    });
  }, [rows, zusatz, tagId, listId, zustandFilter]);

  const alleSichtbarenGewaehlt =
    gefiltert.length > 0 && gefiltert.every((r) => selected.has(r.id));

  const umschaltenAlle = () => {
    setSelected((bisher) => {
      const neu = new Set(bisher);
      for (const r of gefiltert) {
        if (alleSichtbarenGewaehlt) neu.delete(r.id);
        else neu.add(r.id);
      }
      return neu;
    });
  };

  const umschaltenEins = (id: string) => {
    setSelected((bisher) => {
      const neu = new Set(bisher);
      if (neu.has(id)) neu.delete(id);
      else neu.add(id);
      return neu;
    });
  };

  const auswahlLeads = useMemo(
    () => rows.filter((r) => selected.has(r.id)),
    [rows, selected],
  );

  const nachAktion = useCallback(() => {
    setSpeichernAlsListe(false);
    setTaggen(false);
    setUebernehmen(false);
    setSelected(new Set());
    setRefreshKey((k) => k + 1);
  }, []);

  // Kein eigenes catch: schlägt ein Aufruf fehl, soll `ConfirmDialog` das wie
  // überall sonst selbst anzeigen und offen bleiben, statt sich wegzuschließen
  // und die Meldung an anderer Stelle zu verlieren.
  const uebernehmenAusfuehren = async () => {
    await Promise.all(
      auswahlLeads.map((r) => crm.contacts.setLifecycle(r.id, 'kontakt')),
    );
    nachAktion();
  };

  return (
    <div>
      <PageHeader>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[16rem] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Name, E-Mail, Telefon oder Firma …"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              aria-label="Leads durchsuchen"
            />
          </div>

          <select
            className="h-10 rounded-md border border-input bg-card px-3 text-sm"
            value={audienceId}
            onChange={(e) => setAudienceId(e.target.value)}
            aria-label="Zielgruppe"
          >
            <option value="">Alle Zielgruppen</option>
            {audiences.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>

          <select
            className="h-10 rounded-md border border-input bg-card px-3 text-sm"
            value={tagId}
            onChange={(e) => setTagId(e.target.value)}
            aria-label="Tag"
          >
            <option value="">Alle Tags</option>
            {tagsAlle.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>

          <select
            className="h-10 rounded-md border border-input bg-card px-3 text-sm"
            value={listId}
            onChange={(e) => setListId(e.target.value)}
            aria-label="Liste"
          >
            <option value="">Alle Listen</option>
            {listenAktiv.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>

          <select
            className="h-10 rounded-md border border-input bg-card px-3 text-sm"
            value={zustandFilter}
            onChange={(e) => setZustandFilter(e.target.value as Zustand | '')}
            aria-label="Zustand"
          >
            <option value="">Alle Zustände</option>
            {ZUSTAND_REIHENFOLGE.map((z) => (
              <option key={z} value={z}>
                {ZUSTAND_LABEL[z]}
              </option>
            ))}
          </select>
        </div>
      </PageHeader>

      {ladeFehler && (
        <Card className="mb-4 border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {ladeFehler}
        </Card>
      )}

      {selected.size > 0 && (
        <Card className="mb-4 flex flex-wrap items-center gap-3 p-3">
          <span className="text-sm font-medium">
            {selected.size} {selected.size === 1 ? 'Lead' : 'Leads'} ausgewählt
          </span>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setSpeichernAlsListe(true)}>
              <ListPlus className="mr-1.5 h-4 w-4" />
              Auswahl als Liste speichern
            </Button>
            <Button variant="outline" size="sm" onClick={() => setTaggen(true)}>
              <TagsIcon className="mr-1.5 h-4 w-4" />
              Auswahl taggen
            </Button>
            <Button variant="outline" size="sm" onClick={() => setUebernehmen(true)}>
              <UserCheck className="mr-1.5 h-4 w-4" />
              Als Kontakt übernehmen
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              Auswahl aufheben
            </Button>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        {ladeLeads ? (
          <p className="p-6 text-sm text-muted-foreground">Lade …</p>
        ) : gefiltert.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">
            {suche.trim() || audienceId || tagId || listId || zustandFilter
              ? 'Kein Lead passt zu dieser Auswahl.'
              : 'Noch keine Leads. Ein CSV-Import legt sie mit Lebenszyklus „Lead“ an.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={alleSichtbarenGewaehlt}
                      onChange={umschaltenAlle}
                      aria-label="Alle sichtbaren Leads auswählen"
                    />
                  </th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Firma</th>
                  <th className="px-4 py-3 font-medium">Zielgruppe</th>
                  <th className="px-4 py-3 font-medium">Tags</th>
                  <th className="px-4 py-3 font-medium">Listen</th>
                  <th className="px-4 py-3 font-medium">Zustand</th>
                </tr>
              </thead>
              <tbody>
                {gefiltert.map((r) => {
                  const info = zusatz.get(r.id);
                  return (
                    <LeadRow
                      key={r.id}
                      contact={r}
                      audienceLabel={audienceName.get(r.audienceId) ?? '—'}
                      tags={info?.tags}
                      listNames={info?.listIds.map((id) => listName.get(id) ?? '?')}
                      zustand={info?.zustand}
                      selected={selected.has(r.id)}
                      onToggle={() => umschaltenEins(r.id)}
                      onOpen={() => navigate(aktePfad(r.id, 'lead'))}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {gefiltert.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {gefiltert.length} {gefiltert.length === 1 ? 'Lead' : 'Leads'}
        </p>
      )}

      {speichernAlsListe && (
        <SaveAsListDialog
          contactIds={[...selected]}
          audiences={audiences}
          {...(audienceId ? { suggestedAudienceId: audienceId } : {})}
          onClose={() => setSpeichernAlsListe(false)}
          onDone={nachAktion}
        />
      )}

      {taggen && (
        <TagSelectionDialog
          leads={auswahlLeads.map((r) => ({
            id: r.id,
            displayName: r.displayName,
            tags: zusatz.get(r.id)?.tags ?? [],
          }))}
          onClose={() => setTaggen(false)}
          onDone={nachAktion}
        />
      )}

      {uebernehmen && (
        <ConfirmDialog
          title={`${selected.size} ${selected.size === 1 ? 'Lead' : 'Leads'} als Kontakt übernehmen?`}
          confirmLabel="Übernehmen"
          onCancel={() => setUebernehmen(false)}
          onConfirm={uebernehmenAusfuehren}
        >
          <p className="text-sm text-muted-foreground">
            Übernommene Leads verschwinden von dieser Seite und stehen ab dann in der
            Akte ihrer Firma, auffindbar über die Suche — Lead-Listen und Tags
            bleiben dabei erhalten.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
